import {
  copyProjectMembersSelectionOptions,
  copyProjectMembersTabs,
  copyProjectMembersTabOptions,
  copyProjectTeamGroupsSelectionModeOptions
} from '../constants'

const TEAMS_TAB = copyProjectMembersTabOptions.TEAMS
const MEMBERS_TAB = copyProjectMembersTabOptions.MEMBERS

const createTabSelection = ({
  scope = copyProjectMembersSelectionOptions.ALL,
  selectedIds = [],
  teamGroupsMode = copyProjectTeamGroupsSelectionModeOptions.INCLUDE,
  includeTeamGroupMembers = false
} = {}) => ({
  scope,
  selectedIds,
  teamGroupsMode,
  includeTeamGroupMembers
})

const isTeamGroupsTab = (tab) => tab === TEAMS_TAB

const getItemId = (tab, item) => {
  if (tab === MEMBERS_TAB) {
    return item?.userId ?? item?.id
  }

  return item?.id ?? item?.userId
}

const getTabItemIds = (tab, items = []) =>
  items
    .map((item) => getItemId(tab, item))
    .filter((itemId) => itemId !== undefined && itemId !== null)

const orderIdsLikeItems = (itemIds = [], selectedIds = []) => {
  const selectedIdsSet = new Set(selectedIds)

  return itemIds.filter((itemId) => selectedIdsSet.has(itemId))
}

const areIdsEqual = (currentIds = [], nextIds = []) =>
  currentIds.length === nextIds.length &&
  currentIds.every((id, index) => id === nextIds[index])

const getSelectionScope = (selectedCount, totalCount) => {
  if (!totalCount) {
    return copyProjectMembersSelectionOptions.EMPTY
  }

  if (!selectedCount) {
    return copyProjectMembersSelectionOptions.NONE
  }

  if (selectedCount === totalCount) {
    return copyProjectMembersSelectionOptions.ALL
  }

  return copyProjectMembersSelectionOptions.CUSTOM
}

const createAllSelection = (tab, currentSelection = createTabSelection()) =>
  createTabSelection({
    scope: copyProjectMembersSelectionOptions.ALL,
    teamGroupsMode: isTeamGroupsTab(tab)
      ? copyProjectTeamGroupsSelectionModeOptions.EXCLUDE
      : currentSelection.teamGroupsMode,
    includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
  })

const createNoneSelection = (tab, currentSelection = createTabSelection()) =>
  createTabSelection({
    scope: copyProjectMembersSelectionOptions.NONE,
    teamGroupsMode: isTeamGroupsTab(tab)
      ? copyProjectTeamGroupsSelectionModeOptions.INCLUDE
      : currentSelection.teamGroupsMode,
    includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
  })

const createEmptySelection = (currentSelection = createTabSelection()) =>
  createTabSelection({
    scope: copyProjectMembersSelectionOptions.EMPTY,
    teamGroupsMode: currentSelection.teamGroupsMode,
    includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
  })

const getVisibleSelectedIds = (
  tab,
  currentSelection = createTabSelection(),
  items = []
) => {
  const itemIds = getTabItemIds(tab, items)

  if (!itemIds.length) {
    return []
  }

  if (currentSelection.scope === copyProjectMembersSelectionOptions.ALL) {
    return itemIds
  }

  if (
    currentSelection.scope === copyProjectMembersSelectionOptions.NONE ||
    currentSelection.scope === copyProjectMembersSelectionOptions.EMPTY
  ) {
    return []
  }

  const normalizedIds = orderIdsLikeItems(itemIds, currentSelection.selectedIds)

  if (!isTeamGroupsTab(tab)) {
    return normalizedIds
  }

  if (
    currentSelection.teamGroupsMode ===
    copyProjectTeamGroupsSelectionModeOptions.EXCLUDE
  ) {
    const excludedIds = new Set(normalizedIds)

    return itemIds.filter((itemId) => !excludedIds.has(itemId))
  }

  return normalizedIds
}

const buildSelectionFromVisibleIds = (
  tab,
  visibleSelectedIds = [],
  items = [],
  currentSelection = createTabSelection()
) => {
  const itemIds = getTabItemIds(tab, items)

  if (!itemIds.length) {
    return createEmptySelection(currentSelection)
  }

  const normalizedVisibleIds = orderIdsLikeItems(itemIds, visibleSelectedIds)
  const scope = getSelectionScope(normalizedVisibleIds.length, itemIds.length)

  if (scope === copyProjectMembersSelectionOptions.ALL) {
    return createAllSelection(tab, currentSelection)
  }

  if (
    scope === copyProjectMembersSelectionOptions.NONE ||
    scope === copyProjectMembersSelectionOptions.EMPTY
  ) {
    return createNoneSelection(tab, currentSelection)
  }

  if (!isTeamGroupsTab(tab)) {
    return createTabSelection({
      scope: copyProjectMembersSelectionOptions.CUSTOM,
      selectedIds: normalizedVisibleIds,
      teamGroupsMode: currentSelection.teamGroupsMode,
      includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
    })
  }

  const shouldUseExcludeMode =
    currentSelection.scope === copyProjectMembersSelectionOptions.ALL ||
    currentSelection.teamGroupsMode ===
      copyProjectTeamGroupsSelectionModeOptions.EXCLUDE

  if (shouldUseExcludeMode) {
    const visibleIdsSet = new Set(normalizedVisibleIds)
    const excludedIds = itemIds.filter((itemId) => !visibleIdsSet.has(itemId))

    return createTabSelection({
      scope: copyProjectMembersSelectionOptions.CUSTOM,
      selectedIds: excludedIds,
      teamGroupsMode: copyProjectTeamGroupsSelectionModeOptions.EXCLUDE,
      includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
    })
  }

  return createTabSelection({
    scope: copyProjectMembersSelectionOptions.CUSTOM,
    selectedIds: normalizedVisibleIds,
    teamGroupsMode: copyProjectTeamGroupsSelectionModeOptions.INCLUDE,
    includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
  })
}

const normalizeTabSelection = (
  tab,
  currentSelection = createTabSelection(),
  items = []
) => {
  const itemIds = getTabItemIds(tab, items)

  if (!itemIds.length) {
    return createEmptySelection(currentSelection)
  }

  if (
    currentSelection.scope === copyProjectMembersSelectionOptions.ALL ||
    currentSelection.scope === copyProjectMembersSelectionOptions.EMPTY
  ) {
    return createAllSelection(tab, currentSelection)
  }

  if (currentSelection.scope === copyProjectMembersSelectionOptions.NONE) {
    return createNoneSelection(tab, currentSelection)
  }

  return buildSelectionFromVisibleIds(
    tab,
    getVisibleSelectedIds(tab, currentSelection, items),
    items,
    currentSelection
  )
}

const getCheckboxState = (selection) => {
  if (selection.scope === copyProjectMembersSelectionOptions.ALL) {
    return true
  }

  if (selection.scope === copyProjectMembersSelectionOptions.CUSTOM) {
    return 'indeterminate'
  }

  return false
}

const areSelectionsEqual = (
  currentSelectionByTab = {},
  nextSelectionByTab = {}
) =>
  copyProjectMembersTabs.every((tab) => {
    const currentSelection = currentSelectionByTab[tab] ?? createTabSelection()
    const nextSelection = nextSelectionByTab[tab] ?? createTabSelection()

    return (
      currentSelection.scope === nextSelection.scope &&
      currentSelection.teamGroupsMode === nextSelection.teamGroupsMode &&
      currentSelection.includeTeamGroupMembers ===
        nextSelection.includeTeamGroupMembers &&
      areIdsEqual(currentSelection.selectedIds, nextSelection.selectedIds)
    )
  })

export const buildDefaultSelectionByTab = () =>
  copyProjectMembersTabs.reduce((selectionByTab, tab) => {
    selectionByTab[tab] = createTabSelection()

    return selectionByTab
  }, {})

export const cloneSelectionByTab = (selectionByTab = {}) =>
  copyProjectMembersTabs.reduce((nextSelectionByTab, tab) => {
    const currentSelection = selectionByTab?.[tab] ?? createTabSelection()

    nextSelectionByTab[tab] = createTabSelection({
      scope: currentSelection.scope,
      selectedIds: [...(currentSelection.selectedIds ?? [])],
      teamGroupsMode: currentSelection.teamGroupsMode,
      includeTeamGroupMembers: currentSelection.includeTeamGroupMembers
    })

    return nextSelectionByTab
  }, {})

export const getNormalizedSelectionByTab = (
  selectionByTab = {},
  itemsByTab = {}
) =>
  copyProjectMembersTabs.reduce((nextSelectionByTab, tab) => {
    nextSelectionByTab[tab] = normalizeTabSelection(
      tab,
      selectionByTab?.[tab],
      itemsByTab?.[tab]
    )

    return nextSelectionByTab
  }, {})

export const getSelectedItemIdsByTab = (selectionByTab = {}, itemsByTab = {}) =>
  copyProjectMembersTabs.reduce((selectedItemIdsByTab, tab) => {
    selectedItemIdsByTab[tab] = getVisibleSelectedIds(
      tab,
      getNormalizedSelectionByTab(selectionByTab, itemsByTab)[tab],
      itemsByTab?.[tab]
    )

    return selectedItemIdsByTab
  }, {})

export const getCheckboxStateByTab = (selectionByTab = {}, itemsByTab = {}) =>
  copyProjectMembersTabs.reduce((checkboxStateByTab, tab) => {
    checkboxStateByTab[tab] = getCheckboxState(
      getNormalizedSelectionByTab(selectionByTab, itemsByTab)[tab]
    )

    return checkboxStateByTab
  }, {})

export const toggleSelectionItemInSelection = (
  selectionByTab = {},
  itemsByTab = {},
  tab,
  itemId
) => {
  const normalizedSelectionByTab = getNormalizedSelectionByTab(
    selectionByTab,
    itemsByTab
  )
  const currentSelection = normalizedSelectionByTab[tab]
  const selectedIds = new Set(
    getVisibleSelectedIds(tab, currentSelection, itemsByTab?.[tab])
  )

  if (selectedIds.has(itemId)) {
    selectedIds.delete(itemId)
  } else {
    selectedIds.add(itemId)
  }

  return {
    ...normalizedSelectionByTab,
    [tab]: buildSelectionFromVisibleIds(
      tab,
      [...selectedIds],
      itemsByTab?.[tab],
      currentSelection
    )
  }
}

export const toggleSelectionCheckboxInSelection = (
  selectionByTab = {},
  itemsByTab = {},
  tab,
  nextValue
) => {
  const normalizedSelectionByTab = getNormalizedSelectionByTab(
    selectionByTab,
    itemsByTab
  )
  const currentSelection = normalizedSelectionByTab[tab]
  const currentCheckboxState = getCheckboxState(currentSelection)
  const resolvedValue =
    typeof nextValue === 'function'
      ? nextValue(currentCheckboxState)
      : nextValue

  return {
    ...normalizedSelectionByTab,
    [tab]: resolvedValue
      ? createAllSelection(tab, currentSelection)
      : createNoneSelection(tab, currentSelection)
  }
}

export const toggleIncludeTeamGroupMembersInSelection = (
  selectionByTab = {},
  itemsByTab = {},
  nextValue
) => {
  const normalizedSelectionByTab = getNormalizedSelectionByTab(
    selectionByTab,
    itemsByTab
  )
  const currentTeamsSelection = normalizedSelectionByTab[TEAMS_TAB]
  const resolvedValue =
    typeof nextValue === 'function'
      ? nextValue(currentTeamsSelection.includeTeamGroupMembers)
      : nextValue

  return {
    ...normalizedSelectionByTab,
    [TEAMS_TAB]: createTabSelection({
      ...currentTeamsSelection,
      includeTeamGroupMembers: !!resolvedValue
    })
  }
}

export const hasSelectionChanged = (
  currentSelectionByTab = {},
  nextSelectionByTab = {}
) => !areSelectionsEqual(currentSelectionByTab, nextSelectionByTab)
